import type { ReactNode } from "react"
import Link from "next/link"
import { ArrowRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { ToolIllustration } from "@/components/tools/tool-illustration"

interface ToolCrossLinkProps {
  /** Target tool URL. May carry a query string; the illustration keys on the path. */
  href: string
  /** Bold opening sentence of the strip. */
  lead: string
  /** Rest of the sentence after `lead`. */
  body: ReactNode
  /** Pill label naming the action. */
  cta: string
  className?: string
}

/**
 * One-line strip that points from a tool to one sibling tool. Renders the
 * target's ToolIllustration so every /tools/* link uses the same artwork.
 * Server-safe: no client hooks, so it can sit in a server shell or an island.
 */
export function ToolCrossLink({ href, lead, body, cta, className }: ToolCrossLinkProps) {
  const path = href.split("?")[0]

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-4 rounded-2xl border border-gray-200 bg-white px-6 py-4.5 shadow-elevation-1",
        className,
      )}
    >
      <div className="grid h-10 w-10 shrink-0 place-items-center [&_svg]:h-10 [&_svg]:w-10" aria-hidden="true">
        <ToolIllustration href={path} />
      </div>
      <p className="min-w-60 flex-1 text-[14.5px] text-gray-700">
        <strong className="text-gray-900">{lead}</strong> {body}
      </p>
      <Link
        href={href}
        className="press flex min-h-11 items-center gap-2 whitespace-nowrap rounded-full border border-green-200 bg-green-50 px-5 text-sm font-bold text-green-800 hover:border-green-300 hover:bg-green-100"
      >
        {cta}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </div>
  )
}
